import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { COACHES } from "../constants/contact";

// Medallón de una entrenadora: disco con monograma (o foto) dentro de dos
// anillos dorados que giran en sentidos opuestos, con una partícula en órbita.
const Medallion = ({ coach, size = 132, delay = 0 }) => (
  <div className="group relative pointer-events-auto" style={{ width: size, height: size }}>
    <motion.div
      className="absolute inset-0"
      animate={{ y: [0, -9, 0] }}
      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay }}
    >
      <svg className="absolute inset-0 w-full h-full text-[#C5A059]/45" viewBox="0 0 150 150" fill="none">
        <motion.circle
          cx="75" cy="75" r="72" stroke="currentColor" strokeWidth="1" strokeDasharray="4 12" strokeLinecap="round"
          animate={{ rotate: 360 }} transition={{ duration: 24, repeat: Infinity, ease: "linear" }} style={{ transformOrigin: "75px 75px" }}
        />
        <motion.circle
          cx="75" cy="75" r="60" stroke="#C5A059" strokeOpacity="0.15" strokeWidth="1"
          animate={{ rotate: -360 }} transition={{ duration: 30, repeat: Infinity, ease: "linear" }} style={{ transformOrigin: "75px 75px" }}
        />
      </svg>
      <motion.div
        className="absolute inset-0"
        animate={{ rotate: 360 }} transition={{ duration: 9, repeat: Infinity, ease: "linear", delay }} style={{ transformOrigin: "50% 50%" }}
      >
        <span className="absolute left-1/2 -top-[3px] -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#C5A059] shadow-[0_0_10px_#C5A059]" />
      </motion.div>
      <div className="absolute inset-[13px] rounded-full border border-[#C5A059]/50 bg-gradient-to-b from-[#171310] to-[#0a0a0a] flex items-center justify-center overflow-hidden shadow-[0_0_45px_rgba(197,160,89,0.2)] transition-all duration-500 group-hover:shadow-[0_0_70px_rgba(197,160,89,0.42)] group-hover:border-[#C5A059]">
        {coach.photo ? (
          <img src={coach.photo} alt={coach.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        ) : (
          <span className="font-serif text-4xl text-[#C5A059] leading-none select-none">{coach.monogram}</span>
        )}
      </div>
    </motion.div>
  </div>
);

const Ficha = ({ coach, i, disciplinas }) => (
  <motion.div
    className="flex flex-col items-center text-center"
    initial={{ opacity: 0, y: 18 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.35 + i * 0.12, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    data-testid={`menu-coach-${coach.id}`}
  >
    <Medallion coach={coach} delay={i * 0.6} />
    <h3 className="mt-6 font-serif text-2xl lg:text-[1.7rem] coach-shimmer leading-tight">{coach.name}</h3>
    <p className="mt-2 text-[0.55rem] uppercase tracking-[0.3em] text-zinc-500">{disciplinas[coach.discipline]}</p>
  </motion.div>
);

export const MenuCoach = () => {
  const { t } = useLang();
  const c = t.coach;
  return (
    <div
      className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 hidden md:flex flex-col items-center text-center"
      data-testid="menu-coach"
    >
      <motion.p
        className="overline-gold mb-9"
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }}
      >
        {c.overline}
      </motion.p>

      <div className="flex items-start gap-10 lg:gap-16">
        {COACHES.map((coach, i) => (
          <Ficha key={coach.id} coach={coach} i={i} disciplinas={c.disciplines} />
        ))}
      </div>

      <motion.p
        className="mt-9 max-w-[300px] font-serif italic text-sm text-zinc-500 font-light leading-relaxed"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 0.8 }}
      >
        {c.tagline}
      </motion.p>
    </div>
  );
};

/* Versión compacta para móvil, donde no cabe el lienzo grande. */
export const MenuCoachMobile = () => {
  const { t } = useLang();
  const c = t.coach;
  return (
    <div className="flex md:hidden flex-col gap-3 mt-6" data-testid="menu-coach-mobile">
      <p className="text-[0.55rem] uppercase tracking-[0.3em] text-[#C5A059]">{c.overline}</p>
      {COACHES.map((coach) => (
        <div key={coach.id} className="flex items-center gap-3">
          <div className="w-10 h-10 shrink-0 rounded-full border border-[#C5A059]/50 bg-[#0a0a0a] flex items-center justify-center overflow-hidden shadow-[0_0_20px_rgba(197,160,89,0.2)]">
            {coach.photo ? (
              <img src={coach.photo} alt={coach.name} className="w-full h-full object-cover" />
            ) : (
              <span className="font-serif text-sm text-[#C5A059]">{coach.monogram}</span>
            )}
          </div>
          <div>
            <p className="font-serif text-base coach-shimmer leading-tight">{coach.name}</p>
            <p className="text-[0.5rem] uppercase tracking-[0.25em] text-zinc-600">{c.disciplines[coach.discipline]}</p>
          </div>
        </div>
      ))}
    </div>
  );
};
